<template>
  <Transition name="step-fade" mode="out-in">
    <ClassicAdminLoginStep
      v-if="currentStep === STEPS.IDENTIFIER && isAdmin && useClassicIdentifier"
      :loading="isLoading"
      :error="stepError"
      :clear-on-reload="clearInitialFormOnReload"
      @submit-credentials="onClassicCredentials"
      @submit-phone="onIdentifierSubmit"
      @clear-error="clearError"
    />

    <ClassicUserLoginStep
      v-else-if="currentStep === STEPS.IDENTIFIER && !isAdmin && useClassicIdentifier"
      :loading="isLoading"
      :error="stepError"
      :register-url="config.registerUrl"
      :terms-link="config.termsLink"
      :privacy-link="config.privacyLink"
      :clear-on-reload="clearInitialFormOnReload"
      :notice="loginNotice"
      @submit="onIdentifierSubmit"
      @clear-error="clearError"
    />

    <StepIdentifier
      v-else-if="currentStep === STEPS.IDENTIFIER"
      :loading="isLoading"
      :error="stepError"
      :support-phone="config.supportPhone"
      :register-url="config.registerUrl"
      :is-admin="isAdmin"
      :clear-on-reload="clearInitialFormOnReload"
      :notice="loginNotice"
      @submit="onIdentifierSubmit"
    />

    <ClassicUserLoginStep
      v-else-if="currentStep === STEPS.CLASSIC_PHONE"
      phone-only
      :loading="isLoading"
      :error="stepError"
      :register-url="config.registerUrl"
      :terms-link="config.termsLink"
      :privacy-link="config.privacyLink"
      @submit="onIdentifierSubmit"
      @email="onClassicEmailSwitch"
      @clear-error="clearError"
    />

    <ClassicPasswordStep
      v-else-if="currentStep === STEPS.CLASSIC_PASSWORD"
      :identifier="flowData.identifier"
      :loading="isLoading"
      :error="stepError"
      :register-url="config.registerUrl"
      :terms-link="config.termsLink"
      :privacy-link="config.privacyLink"
      :is-admin="isAdmin"
      @submit="onClassicCredentials"
      @forgot="onClassicForgotPassword"
      @phone="onClassicPhoneSwitch"
    />

    <ClassicPhoneCodeStep
      v-else-if="currentStep === STEPS.CLASSIC_VERIFY"
      :loading="isLoading"
      :error="stepError"
      :is-cooling-down="mfaCooldown.isCoolingDown"
      :countdown="mfaCooldown.formattedCountdown"
      :is-admin="isAdmin"
      :register-url="config.registerUrl"
      :terms-link="config.termsLink"
      :privacy-link="config.privacyLink"
      @submit="onClassicCodeVerified"
      @resend="onClassicCodeResend"
      @back="onClassicEmailSwitch"
      @clear-error="clearError"
    />

    <StepSignInMethod
      v-else-if="magicLinkLoginEnabled && !isAdmin && currentStep === STEPS.SIGN_IN_METHOD"
      :loading="isLoading"
      :error="stepError"
      :support-phone="config.supportPhone"
      :identifier-type="flowData.identifierType"
      @magic-link="onSendMagicLink"
      @password="clearError(); navigateTo(STEPS.PASSWORD)"
      @cancel="onSignInMethodBack"
    />

    <StepMagicLinkSent
      v-else-if="magicLinkLoginEnabled && !isAdmin && currentStep === STEPS.MAGIC_LINK_SENT"
      :identifier="flowData.identifier"
      :identifier-type="flowData.identifierType"
      :channel="flowData.magicLinkChannel"
      :loading="isLoading"
      :error="stepError"
      :feedback="magicLinkFeedback"
      :is-cooling-down="magicLinkCooldown.isCoolingDown"
      :countdown="magicLinkCooldown.formattedCountdown"
      :support-phone="config.supportPhone"
      :expires-in-minutes="config.magicLinkTtlMinutes"
      :magic-link-expired="flowData.magicLinkExpired"
      :is-trusted-device="flowData.isTrustedDevice"
      @resend="onResendMagicLink"
      @back="onMagicLinkBack"
    />

    <StepPassword
      v-else-if="currentStep === STEPS.PASSWORD"
      :identifier="flowData.identifier"
      :loading="isLoading"
      :error="stepError"
      :trust-device="flowData.trustDevice"
      :bypass-mfa="flowData.bypassMfa"
      :register-url="config.registerUrl"
      :support-phone="config.supportPhone"
      :is-admin="isAdmin"
      :show-magic-link="magicLinkLoginEnabled && flowData.identifierType === 'email'"
      :is-password-reset="flowData.isPasswordReset"
      :password-reset-channel="flowData.passwordResetChannel"
      @submit="onPasswordSubmit"
      @update:trust-device="setFlowData({ trustDevice: $event })"
      @forgot="onForgotPassword"
      @magic-link="onSendMagicLink"
      @cancel="onPasswordBack"
    />

    <ClassicForgotPasswordStep
      v-else-if="currentStep === STEPS.FORGOT_PASSWORD && flowData.flowMode === 'classic'"
      :identifier="flowData.identifier"
      :loading="isLoading"
      :error="stepError"
      :register-url="config.registerUrl"
      @submit="onClassicForgotSubmit"
      @clear-error="clearError"
    />

    <StepForgotPassword
      v-else-if="currentStep === STEPS.FORGOT_PASSWORD"
      :loading="isLoading"
      :error="stepError"
      :is-cooling-down="resetLinkCooldown.isCoolingDown"
      :countdown="resetLinkCooldown.formattedCountdown"
      :support-phone="config.supportPhone"
      :is-admin="isAdmin"
      :password-reset-required="flowData.passwordResetRequired"
      @send-link="onForgotSendResetLink"
      @cancel="onForgotPasswordBack"
    />

    <ClassicResetLinkSentStep
      v-else-if="currentStep === STEPS.RESET_LINK_SENT && flowData.flowMode === 'classic'"
      :identifier="flowData.identifier"
      :loading="isLoading"
      :error="stepError"
      :feedback="resetLinkFeedback"
      :is-cooling-down="resetLinkCooldown.isCoolingDown"
      :countdown="resetLinkCooldown.formattedCountdown"
      :register-url="config.registerUrl"
      @resend="onResendResetLink"
    />

    <StepResetLinkSent
      v-else-if="currentStep === STEPS.RESET_LINK_SENT"
      :identifier="flowData.identifier"
      :channel="flowData.resetLinkChannel"
      :loading="isLoading"
      :error="stepError"
      :feedback="resetLinkFeedback"
      :is-cooling-down="resetLinkCooldown.isCoolingDown"
      :countdown="resetLinkCooldown.formattedCountdown"
      :support-phone="config.supportPhone"
      :is-admin="isAdmin"
      :ttl-minutes="config.passwordResetTtlMinutes"
      @resend="onResendResetLink"
      @send-alternative="onSendResetLinkAlternative"
      @back="onResetLinkBack"
    />

    <ClassicSetPasswordStep
      v-else-if="currentStep === STEPS.SET_PASSWORD_FROM_LINK && flowData.flowMode === 'classic'"
      :email="passwordContext.email || flowData.identifier"
      :loading="isLoading"
      :password-error="setPasswordErrors.password"
      :confirmation-error="setPasswordErrors.password_confirmation"
      :form-error="setPasswordErrors.form"
      :expired-token-error="setPasswordErrors.expiredToken"
      :is-admin="isAdmin"
      :register-url="config.registerUrl"
      @submit="onSetPasswordFromLink"
      @clear-error="clearSetPasswordError"
      @request-new-reset="onRequestNewPasswordReset"
    />

    <StepSetPasswordFromLink
      v-else-if="currentStep === STEPS.SET_PASSWORD_FROM_LINK"
      :loading="isLoading"
      :password-reset-required="flowData.passwordResetRequired"
      :password-error="setPasswordErrors.password"
      :confirmation-error="setPasswordErrors.password_confirmation"
      :form-error="setPasswordErrors.form"
      :expired-token-error="setPasswordErrors.expiredToken"
      :support-phone="config.supportPhone"
      :is-admin="isAdmin"
      :password-context="passwordContext"
      @submit="onSetPasswordFromLink"
      @clear-error="clearSetPasswordError"
      @request-new-reset="onRequestNewPasswordReset"
    />

    <StepChooseMethod
      v-else-if="currentStep === STEPS.CHOOSE_METHOD"
      :loading="isLoading"
      :error="stepError"
      :hide-trust-option="flowData.trustDevice"
      :support-phone="config.supportPhone"
      :is-admin="isAdmin"
      :show-magic-link="magicLinkLoginEnabled"
      @submit="onMethodChosen"
      @magic-link="onSendMagicLink"
      @cancel="clearError(); navigateTo(STEPS.PASSWORD)"
    />

    <StepVerifyCode
      v-else-if="currentStep === STEPS.VERIFY"
      :method="flowData.preferredMethod"
      :show-trust-option="isAdmin || !flowData.trustDevice"
      :trust-device="flowData.trustDevice"
      :loading="isLoading"
      :error="stepError"
      :is-cooling-down="mfaCooldown.isCoolingDown"
      :countdown="mfaCooldown.formattedCountdown"
      :support-phone="config.supportPhone"
      :is-admin="isAdmin"
      :identifier-type="flowData.identifierType"
      @submit="onCodeVerified"
      @resend="onResendCode"
      @cancel="onVerifyCancel"
      @clear-error="clearError"
    />

  </Transition>
</template>

<script setup>
import { computed, onMounted, provide, ref, shallowRef, watch } from 'vue'
import { authApi } from './services/authApi.js'
import { STEPS } from './constants/steps.js';
import { useLoginFlow } from './composables/useLoginFlow.js'
import { useMfa } from './composables/useMfa.js'
import { useResendCooldown } from '../shared/composables/useResendCooldown.js'
import { useLoginAccessibility } from './composables/useLoginAccessibility.js'
import { useMagicLinkSessionPoll } from './composables/useMagicLinkSessionPoll.js'
import { initAuthTawk, setAuthTawkBubbleVisible } from '../shared/composables/useAuthTawk.js'
import StepIdentifier     from './steps/StepIdentifier.vue'
import StepSignInMethod   from './steps/StepSignInMethod.vue'
import StepMagicLinkSent  from './steps/StepMagicLinkSent.vue'
import StepPassword       from './steps/StepPassword.vue'
import StepForgotPassword from './steps/StepForgotPassword.vue'
import StepResetLinkSent  from './steps/StepResetLinkSent.vue'
import StepSetPasswordFromLink from './steps/StepSetPasswordFromLink.vue'
import StepChooseMethod   from './steps/StepChooseMethod.vue'
import StepVerifyCode     from './steps/StepVerifyCode.vue'
import ClassicPasswordStep from '../classic/login/ClassicPasswordStep.vue'
import ClassicPhoneCodeStep from '../classic/login/ClassicPhoneCodeStep.vue'
import ClassicAdminLoginStep from '../classic/login/ClassicAdminLoginStep.vue'
import ClassicUserLoginStep from '../classic/login/ClassicUserLoginStep.vue'
import ClassicForgotPasswordStep from '../classic/password/ClassicForgotPasswordStep.vue'
import ClassicResetLinkSentStep from '../classic/password/ClassicResetLinkSentStep.vue'
import ClassicSetPasswordStep from '../classic/password/ClassicSetPasswordStep.vue'

const props = defineProps({
  config: { type: Object, required: true },
})

const isAdmin = computed(() => props.config.guard === 'admin')
const passwordContext = computed(() => props.config.passwordContext || window.authPasswordContext || {})
const clearInitialFormOnReload = window.performance
  ?.getEntriesByType?.('navigation')
  ?.some(entry => entry.type === 'reload') ?? false

const defaultPostLoginUrl = () => (isAdmin.value ? '/admin/tests' : '/overview')

provide('authLegalLinks', computed(() => ({
  termsLink:   props.config.termsLink,
  privacyLink: props.config.privacyLink,
  consentLink: props.config.consentLink,
})))

provide('authBrandLogoUrl', computed(() => props.config.brandLogoUrl || '/images/global/brand-logo.svg'))

const MAGIC_LINK_COOLDOWN_SECONDS = 60
const MFA_COOLDOWN_SECONDS = 60
const TAWK_EMBED_SRC = 'https://embed.tawk.to/5f4b25a51e7ade5df4452367/default'
const EXPIRED_PASSWORD_RESET_MESSAGE = 'This password request link has expired. Please request a new one by clicking this link: Request a new password reset link'

const { currentStep, isLoading, flowData, navigateTo, setFlowData } = useLoginFlow()
const magicLinkLoginEnabled = computed(() => flowData.mfaEnabled)
const useClassicIdentifier = computed(() => {
  if (!isAdmin.value) return !props.config.modernLoginDesignEnabled
  if (flowData.identifier) return flowData.flowMode === 'classic'
  return props.config.initialFlowMode === 'classic'
})
const { stepError, extractError, extractFieldError, extractFirstError, clearError } = useMfa()
const { announcement } = useLoginAccessibility(currentStep, stepError)

provide('authLoginAnnouncement', announcement)
const magicLinkCooldown = useResendCooldown(MAGIC_LINK_COOLDOWN_SECONDS)
const mfaCooldown = useResendCooldown(MFA_COOLDOWN_SECONDS)
const resetLinkCooldown = useResendCooldown(MAGIC_LINK_COOLDOWN_SECONDS)
const magicLinkFeedback = ref('')
const resetLinkFeedback = ref('')
const loginNotice = shallowRef(isAdmin.value ? null : props.config.notice)
const setPasswordErrors = ref({
  password: '',
  password_confirmation: '',
  form: '',
  expiredToken: false,
})

const magicLinkSessionPoll = useMagicLinkSessionPoll(
  authApi.getSessionStatus,
  redirect => window.location.replace(redirect || defaultPostLoginUrl()),
  redirect => window.location.replace(redirect),
)

watch(currentStep, step => {
  if (magicLinkLoginEnabled.value && step === STEPS.MAGIC_LINK_SENT) {
    magicLinkSessionPoll.start()
  } else {
    magicLinkSessionPoll.stop()
  }
}, { immediate: true })

initAuthTawk({
  embedSrc: TAWK_EMBED_SRC,
  guard: props.config.guard,
})
setAuthTawkBubbleVisible(true)

const handleRateLimit = (error, cooldown, fallbackMessage) => {
  const retryAfter = error.response?.data?.retry_after ?? 60
  cooldown.startCooldown(retryAfter)
  stepError.value = error.response?.data?.message ?? fallbackMessage
}

const handleMagicLinkRateLimit = (error) => {
  handleRateLimit(error, magicLinkCooldown, 'Please wait before requesting another link.')
}

const handleMfaRateLimit = (error) => {
  handleRateLimit(error, mfaCooldown, 'Please wait before requesting another code.')
}

const onMagicLinkBack = () => {
  magicLinkFeedback.value = ''
  clearError()
  navigateTo(magicLinkLoginEnabled.value ? STEPS.SIGN_IN_METHOD : STEPS.PASSWORD)
}

const onSignInMethodBack = () => {
  clearError()
  navigateTo(STEPS.IDENTIFIER)
}

const onPasswordBack = () => {
  clearError()
  if (isAdmin.value) {
    navigateTo(STEPS.IDENTIFIER)
  } else if (magicLinkLoginEnabled.value && flowData.identifierType === 'email') {
    navigateTo(STEPS.SIGN_IN_METHOD)
  } else {
    navigateTo(STEPS.IDENTIFIER)
  }
}

const onIdentifierSubmit = async (identifier) => {
  isLoading.value = true
  clearError()
  loginNotice.value = null
  try {
    const { data } = await authApi.startLogin(identifier, props.config.guard)

    setFlowData({
      identifier,
      identifierType:       data.type,
      userType:             data.user_type,
      hasPreferredMethod:   data.has_preferred_method,
      preferredMethod:      data.preferred_method ?? null,
      mfaEnabled:           data.mfa_enabled ?? true,
      bypassMfa:            Boolean(data.bypass_mfa),
      trustDevice:          false,
      magicLinkChannel:     data.type,
      passwordResetRequired: false,
      flowMode:             data.flow_mode ?? 'modern',
      isPasswordReset:      false,
      passwordResetChannel: '',
    })

    if (data.flow_mode === 'classic') {
      if (data.type === 'phone') {
        await authApi.sendClassicPhoneCode(identifier, props.config.guard)
        mfaCooldown.startCooldown()
        navigateTo(STEPS.CLASSIC_VERIFY)
      } else {
        navigateTo(STEPS.CLASSIC_PASSWORD)
      }
      return
    }

    if (isAdmin.value) {
      navigateTo(STEPS.PASSWORD)
    } else if (
      magicLinkLoginEnabled.value
      && (data.type === 'email' || data.type === 'phone')
    ) {
      navigateTo(STEPS.SIGN_IN_METHOD)
    } else if (data.mfa_enabled === false) {
      navigateTo(STEPS.PASSWORD)
    } else {
      await authApi.sendCode(data.preferred_method, identifier, props.config.guard)
      mfaCooldown.startCooldown()
      setFlowData({ verifyBackStep: STEPS.IDENTIFIER })
      navigateTo(STEPS.VERIFY)
    }
  } catch (e) {
    if (e.response?.status === 429) {
      handleMfaRateLimit(e)
    } else {
      stepError.value = extractError(e, 'identifier')
    }
  } finally {
    isLoading.value = false
  }
}

const onSendMagicLink = async () => {
  if (!magicLinkLoginEnabled.value || isAdmin.value) return

  isLoading.value = true
  clearError()
  magicLinkFeedback.value = ''
  try {
    const { data } = await authApi.sendMagicLink(flowData.identifier, null, props.config.guard)

    if (showRequiredPasswordReset(data)) {
      return
    }

    setFlowData({
      magicLinkChannel: data.channel,
      isTrustedDevice: data.is_trusted_device,
    })

    magicLinkCooldown.startCooldown()
    navigateTo(STEPS.MAGIC_LINK_SENT)
  } catch (e) {
    if (e.response?.status === 429) {
      handleMagicLinkRateLimit(e)
    } else {
      stepError.value = extractError(e, 'identifier')
    }
  } finally {
    isLoading.value = false
  }
}

const onResendMagicLink = async () => {
  if (!magicLinkLoginEnabled.value || magicLinkCooldown.isCoolingDown) return

  isLoading.value = true
  clearError()
  try {
    const { data } = await authApi.sendMagicLink(flowData.identifier, null, props.config.guard)

    if (showRequiredPasswordReset(data)) {
      return
    }

    setFlowData({
      magicLinkChannel: data.channel,
    })

    magicLinkFeedback.value = 'A new link has been sent. Please check your inbox.'
    magicLinkCooldown.startCooldown()
  } catch (e) {
    if (e.response?.status === 429) {
      handleMagicLinkRateLimit(e)
    } else {
      stepError.value = extractError(e, 'identifier')
    }
  } finally {
    isLoading.value = false
  }
}

const onPasswordSubmit = async (password) => {
  isLoading.value = true
  clearError()
  try {
    const { data } = await authApi.verifyPassword(
      flowData.identifier,
      password,
      props.config.guard,
      flowData.trustDevice,
    )

    setFlowData({
      isPasswordReset: false,
    })

    if (showRequiredPasswordReset(data)) {
      return
    }

    if (!data.requires_mfa) {
      window.location.href = data.redirect || defaultPostLoginUrl()
      return
    }

    const hasPreferredMethod = data.has_preferred_method ?? flowData.hasPreferredMethod
    const preferredMethod = data.preferred_method ?? flowData.preferredMethod

    setFlowData({
      hasPreferredMethod,
      preferredMethod,
    })

    if (hasPreferredMethod) {
      await authApi.sendCode(preferredMethod, flowData.identifier, props.config.guard)
      mfaCooldown.startCooldown()
      setFlowData({ verifyBackStep: STEPS.PASSWORD })
      navigateTo(STEPS.VERIFY)
    } else {
      navigateTo(STEPS.CHOOSE_METHOD)
    }
  } catch (e) {
    if (e.response?.status === 429) {
      handleMfaRateLimit(e)
    } else {
      stepError.value = extractError(e, 'password')
    }
  } finally {
    isLoading.value = false
  }
}

const onClassicCredentials = async ({ identifier, password }) => {
  isLoading.value = true
  clearError()

  try {
    const { data: startData } = await authApi.startLogin(identifier, props.config.guard)

    if (startData.flow_mode !== 'classic' || startData.type !== 'email') {
      stepError.value = 'This account uses the modern sign-in flow. Please refresh and try again.'
      return
    }

    setFlowData({
      identifier,
      identifierType: 'email',
      userType: startData.user_type,
      flowMode: 'classic',
      mfaEnabled: false,
      passwordResetRequired: false,
    })

    const { data } = await authApi.verifyPassword(identifier, password, props.config.guard, false)

    if (showRequiredPasswordReset(data)) return

    window.location.href = data.redirect || defaultPostLoginUrl()
  } catch (error) {
    stepError.value = extractError(error, 'password')
  } finally {
    isLoading.value = false
  }
}

const onClassicForgotPassword = (identifier) => {
  clearError()
  setFlowData({
    identifier,
    identifierType: 'email',
    flowMode: 'classic',
    passwordResetRequired: false,
  })
  navigateTo(STEPS.FORGOT_PASSWORD)
}

const showRequiredPasswordReset = (data) => {
  if (!data.requires_password_reset) {
    return false
  }

  const resetToken = data.reset_token ?? ''

  setFlowData({
    passwordResetRequired: true,
    resetToken,
  })
  navigateTo(resetToken ? STEPS.SET_PASSWORD_FROM_LINK : STEPS.FORGOT_PASSWORD)

  return true
}

const onForgotPassword = () => {
  clearError()

  if (!isAdmin.value) {
    navigateTo(STEPS.FORGOT_PASSWORD)
    return
  }

  onForgotSendResetLink('email')
}

const onForgotPasswordBack = () => {
  clearError()
  setFlowData({ passwordResetRequired: false })
  navigateTo(flowData.flowMode === 'classic' ? STEPS.IDENTIFIER : STEPS.PASSWORD)
}

const handleResetLinkRateLimit = (error) => {
  handleRateLimit(error, resetLinkCooldown, 'Please wait before requesting another link.')
}

const sendPasswordResetLink = async (channel) => {
  const { data } = await authApi.sendPasswordResetLink(
    flowData.identifier,
    channel,
    props.config.guard,
  )
  setFlowData({ resetLinkChannel: data.channel })
  resetLinkCooldown.startCooldown()
}

const onForgotSendResetLink = async (channel) => {
  isLoading.value = true
  clearError()
  resetLinkFeedback.value = ''
  try {
    await sendPasswordResetLink(channel)

    setFlowData({
      passwordResetChannel: channel,
    })

    navigateTo(STEPS.PASSWORD)
  } catch (e) {
    if (e.response?.status === 429) {
      handleResetLinkRateLimit(e)
    } else {
      stepError.value = extractError(e, 'identifier')
    }
  } finally {
    isLoading.value = false
  }
}

const onClassicForgotSubmit = async (identifier) => {
  if (resetLinkCooldown.isCoolingDown) return

  isLoading.value = true
  clearError()
  resetLinkFeedback.value = ''

  try {
    const { data } = await authApi.startLogin(identifier, props.config.guard)

    if (data.flow_mode !== 'classic' || data.type !== 'email') {
      stepError.value = 'This account uses the modern password reset flow. Please return to login and try again.'
      return
    }

    setFlowData({
      identifier,
      identifierType: 'email',
      userType: data.user_type,
      flowMode: 'classic',
    })
    await sendPasswordResetLink('email')
    navigateTo(STEPS.RESET_LINK_SENT)
  } catch (error) {
    if (error.response?.status === 429) {
      handleResetLinkRateLimit(error)
    } else {
      stepError.value = extractError(error, 'identifier')
    }
  } finally {
    isLoading.value = false
  }
}

const onResendResetLink = async () => {
  if (resetLinkCooldown.isCoolingDown) return

  isLoading.value = true
  clearError()
  try {
    await sendPasswordResetLink(flowData.resetLinkChannel)
    resetLinkFeedback.value = flowData.resetLinkChannel === 'sms'
        ? 'A new link has been sent to your mobile phone.'
        : 'A new link has been sent to your email.';
  } catch (e) {
    if (e.response?.status === 429) {
      handleResetLinkRateLimit(e)
    } else {
      stepError.value = extractError(e, 'identifier')
    }
  } finally {
    isLoading.value = false
  }
}

const onSendResetLinkAlternative = async () => {
  if (resetLinkCooldown.isCoolingDown) return

  isLoading.value = true
  clearError()
  try {
    const alternativeChannel = flowData.resetLinkChannel === 'email' ? 'sms' : 'email'
    const { data } = await authApi.sendPasswordResetLink(
      flowData.identifier,
      alternativeChannel,
      props.config.guard,
    )
    setFlowData({ resetLinkChannel: data.channel })
    resetLinkFeedback.value = alternativeChannel === 'sms'
      ? 'A new link has been sent to your mobile phone.'
      : 'A new link has been sent to your email.'
    resetLinkCooldown.startCooldown()
  } catch (e) {
    if (e.response?.status === 429) {
      handleResetLinkRateLimit(e)
    } else {
      stepError.value = extractError(e, 'identifier')
    }
  } finally {
    isLoading.value = false
  }
}

const onResetLinkBack = () => {
  resetLinkFeedback.value = ''
  clearError()
  if (flowData.flowMode === 'classic') {
    navigateTo(isAdmin.value ? STEPS.IDENTIFIER : STEPS.FORGOT_PASSWORD)
    return
  }

  navigateTo(isAdmin.value ? STEPS.PASSWORD : STEPS.FORGOT_PASSWORD)
}

const resetSetPasswordErrors = () => {
  setPasswordErrors.value = {
    password: '',
    password_confirmation: '',
    form: '',
    expiredToken: false,
  }
}

const clearSetPasswordError = (field) => {
  if (!field) {
    resetSetPasswordErrors()
    return
  }

  setPasswordErrors.value = {
    ...setPasswordErrors.value,
    [field]: '',
    form: setPasswordErrors.value.expiredToken ? setPasswordErrors.value.form : '',
  }
}

const setPasswordErrorsFromResponse = (error) => {
  const password = extractFieldError(error, 'password')
  const passwordConfirmation = extractFieldError(error, 'password_confirmation')
  const token = extractFieldError(error, 'token')
  const fallback = extractFirstError(error, 'Unable to reset password. Please try again.')
  const isValidationError = error.response?.status === 422

  setPasswordErrors.value = {
    password: password || (!passwordConfirmation && !token && isValidationError ? fallback : ''),
    password_confirmation: passwordConfirmation,
    form: token || (!password && !passwordConfirmation && !isValidationError ? fallback : ''),
    expiredToken: token === EXPIRED_PASSWORD_RESET_MESSAGE,
  }
}

const onRequestNewPasswordReset = async () => {
  const identifier = passwordContext.value.email || flowData.identifier

  clearError()

  if (!identifier) {
    resetSetPasswordErrors()
    setFlowData({
      passwordResetRequired: false,
      resetToken: '',
    })
    navigateTo(STEPS.IDENTIFIER)
    return
  }

  isLoading.value = true

  try {
    const { data } = await authApi.startLogin(identifier, props.config.guard)

    resetSetPasswordErrors()
    setFlowData({
      identifier,
      identifierType: data.type,
      userType: data.user_type,
      flowMode: data.flow_mode,
      passwordResetRequired: false,
      resetToken: '',
    })

    if (isAdmin.value) {
      await sendPasswordResetLink('email')
      navigateTo(STEPS.RESET_LINK_SENT)
      return
    }

    navigateTo(STEPS.FORGOT_PASSWORD)
  } catch (error) {
    resetSetPasswordErrors()
    stepError.value = extractError(error, 'identifier')
    setFlowData({
      passwordResetRequired: false,
      resetToken: '',
    })
    navigateTo(STEPS.IDENTIFIER)
  } finally {
    isLoading.value = false
  }
}

const onSetPasswordFromLink = async (payload) => {
  isLoading.value = true
  clearError()
  resetSetPasswordErrors()
  try {
    const token = flowData.resetToken || props.config.resetToken
    const { data } = await authApi.resetPassword(
      token,
      payload.password,
      payload.password_confirmation,
      props.config.guard,
    )

    if (data.requires_email_verification) {
      setPasswordErrors.value = {
        password: '',
        password_confirmation: '',
        form: data.message || 'Please verify your email address before logging in',
        expiredToken: false,
      }
      return
    }

    if (!data.requires_mfa) {
      window.location.href = data.redirect || defaultPostLoginUrl()
      return
    }

    setFlowData({
      identifier: data.identifier,
      identifierType: data.identifier_type,
      isPasswordReset: true,
      bypassMfa: Boolean(data.bypass_mfa),
    })

    navigateTo(STEPS.PASSWORD)
  } catch (e) {
    setPasswordErrorsFromResponse(e)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {

  if (props.config.resetToken) {
    setFlowData({
      resetToken: props.config.resetToken,
      flowMode: props.config.resetFlowMode || props.config.initialFlowMode || 'modern',
    })
    navigateTo(STEPS.SET_PASSWORD_FROM_LINK)
    return;
  }

  if (props.config.magicLinkIdentifier) {
    setFlowData({
      identifier: props.config.magicLinkIdentifier,
      magicLinkExpired: true,
    })

    navigateTo(STEPS.MAGIC_LINK_SENT)
    return;
  }

  // Navigate to default here, to avoid flashing the identifier screen in the other 2 cases above.
  if (! currentStep.value) {
    navigateTo(STEPS.IDENTIFIER)
  }
})

const onMethodChosen = async (payload) => {
  isLoading.value = true
  clearError()
  try {
    await authApi.sendCode(payload.method, flowData.identifier, props.config.guard)
    mfaCooldown.startCooldown()
    setFlowData({
      preferredMethod: payload.method,
      trustDevice: flowData.trustDevice || payload.trust,
      verifyBackStep: STEPS.CHOOSE_METHOD,
    })
    navigateTo(STEPS.VERIFY)
  } catch (e) {
    if (e.response?.status === 429) {
      handleMfaRateLimit(e)
    } else {
      stepError.value = extractError(e, 'method')
    }
  } finally {
    isLoading.value = false
  }
}

const onCodeVerified = async (payload) => {
  isLoading.value = true
  clearError()
  try {
    const { data } = await authApi.verifyCode(
      payload.code,
      flowData.identifier,
      props.config.guard,
      payload.trust,
    )

    if (showRequiredPasswordReset(data)) {
      return
    }

    window.location.href = data.redirect || defaultPostLoginUrl()
  } catch (e) {
    stepError.value = extractError(e, 'code')
  } finally {
    isLoading.value = false
  }
}

const onResendCode = async () => {
  if (mfaCooldown.isCoolingDown) return

  isLoading.value = true
  clearError()
  try {
    await authApi.sendCode(flowData.preferredMethod, flowData.identifier, props.config.guard)
    mfaCooldown.startCooldown()
  } catch (e) {
    if (e.response?.status === 429) {
      handleMfaRateLimit(e)
    } else {
      stepError.value = extractError(e, 'method')
    }
  } finally {
    isLoading.value = false
  }
}

const onVerifyCancel = () => {
  clearError()
  navigateTo(flowData.verifyBackStep || STEPS.PASSWORD)
}

const onClassicCodeVerified = async (code) => {
  isLoading.value = true
  clearError()
  try {
    const { data } = await authApi.verifyClassicPhoneCode(
      code,
      flowData.identifier,
      props.config.guard,
    )

    if (showRequiredPasswordReset(data)) {
      return
    }

    window.location.href = data.redirect || defaultPostLoginUrl()
  } catch (e) {
    stepError.value = extractError(e, 'code')
  } finally {
    isLoading.value = false
  }
}

const onClassicCodeResend = async () => {
  if (mfaCooldown.isCoolingDown) return

  isLoading.value = true
  clearError()
  try {
    await authApi.sendClassicPhoneCode(flowData.identifier, props.config.guard)
    mfaCooldown.startCooldown()
  } catch (e) {
    if (e.response?.status === 429) {
      handleMfaRateLimit(e)
    } else {
      stepError.value = extractError(e, 'identifier')
    }
  } finally {
    isLoading.value = false
  }
}

const onClassicPhoneSwitch = () => {
  clearError()
  navigateTo(STEPS.CLASSIC_PHONE)
}

const onClassicEmailSwitch = () => {
  clearError()
  setFlowData({
    identifier: '',
    identifierType: 'email',
    flowMode: 'classic',
  })
  navigateTo(STEPS.CLASSIC_PASSWORD)
}
</script>

<style scoped>
.step-fade-enter-active,
.step-fade-leave-active {
  transition: opacity 0.2s ease;
}

@media (prefers-reduced-motion: reduce) {
  .step-fade-enter-active,
  .step-fade-leave-active {
    transition: none;
  }
}
.step-fade-enter-from,
.step-fade-leave-to {
  opacity: 0;
}
</style>
